<template>
  <ion-page>
    <ion-header>
      <ion-toolbar class="overflow-visible">
        <ion-title class="overflow-visible">
          <div class="flex items-center gap-2 justify-center md:justify-start py-1 pl-1">
            <div class="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-md transform-gpu origin-center hover:scale-105 transition-transform overflow-visible">
              <span class="text-xl">🗺️</span>
            </div>
            <span class="hidden sm:inline font-bold text-lg bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent dark:from-blue-400 dark:to-indigo-400">
              {{ appName }}
            </span>
          </div>
        </ion-title>
        <ion-buttons slot="end">
          <ion-button v-if="!authStore.isAuthenticated" @click="router.push('/auth')">
            <ion-icon :icon="personOutline"></ion-icon>
          </ion-button>
          <template v-else>
            <TripMenu />
            <ion-button @click="router.push('/profile')">
              <ion-icon :icon="personOutline"></ion-icon>
            </ion-button>
          </template>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content>
      <!-- Demo Mode Banner - Bottom Left -->
      <div 
        v-if="!authStore.isAuthenticated" 
        class="fixed bottom-4 left-4 z-[1000] bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-3 rounded-lg shadow-2xl max-w-xs lg:bottom-20"
      >
        <div class="flex items-center gap-2 mb-2">
          <ion-icon :icon="eyeOutline" class="text-xl"></ion-icon>
          <p class="text-sm font-bold">
            {{ t('viewingDemo') }}
          </p>
        </div>
        <p class="text-xs opacity-90 mb-2">
          {{ t('signInToCreate') }}
        </p>
        <ion-button 
          @click="router.push('/auth')"
          color="light"
          size="small"
          expand="block"
        >
          <ion-icon :icon="personOutline" slot="start"></ion-icon>
          {{ t('signIn') }}
        </ion-button>
      </div>

      <!-- No trip warning -->
      <div 
        v-if="!currentTrip && authStore.isAuthenticated" 
        class="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 bg-blue-100 dark:bg-blue-900 border border-blue-400 dark:border-blue-600 text-blue-900 dark:text-blue-100 px-4 py-2 rounded-lg shadow-lg"
      >
        <p class="text-sm font-medium">
          ℹ️ {{ createFirstTripText }}
        </p>
      </div>
      
      <div class="home-layout">
        <!-- Map with horizontal stops timeline -->
        <div class="map-section-wrapper">
          <!-- Horizontal stops timeline (desktop only) - NOW ON TOP -->
          <div v-if="displayStops.length > 0" class="timeline-section">
            <StopsTimeline
              :stops="displayStops"
              :calculation="displayCalculation"
              @stop-click="handleStopClick"
              @edit-stop="handleEditStop"
              @delete-stop="handleDeleteStop"
            />
          </div>
          
          <div class="map-section">
            <MapView
              ref="mapRef"
              :stops="displayStops"
              :readonly="!currentTrip || isInDemoMode"
              @map-click="handleMapClick"
              @stop-click="handleStopClick"
              @poi-add-stop="handleAddPOIAsStop"
            />

            <button
              v-if="displayStops.length > 1"
              type="button"
              @click="resetRouteView"
              class="absolute top-4 right-4 z-[1100] rounded-full border border-gray-200 dark:border-gray-600 bg-white/95 dark:bg-gray-800/95 px-4 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-lg backdrop-blur-sm"
              title="Teljes útvonal"
            >
              Teljes utvonal
            </button>
          </div>
        </div>

        <!-- Right sidebar with trip settings (desktop) -->
        <div class="settings-sidebar">
          <TripSettings
            :stops="displayStops"
            :calculation="displayCalculation"
            :vehicles="vehicleStore.vehicles"
            :selected-vehicle-id="currentTrip?.vehicleId ?? null"
            @update:vehicle-id="handleVehicleChange"
            @add-vehicle="openVehicleModal"
          />
        </div>
      </div>

      <!-- Mobile sidebar -->
      <ion-modal
        v-if="showMobileSidebar"
        :is-open="showMobileSidebar"
        @didDismiss="showMobileSidebar = false"
        presentation-style="sheet"
        initial-breakpoint="0.5"
        :breakpoints="[0, 0.5, 0.75, 1]"
      >
        <StopsSidebar
          :stops="displayStops"
          :calculation="displayCalculation"
          @stop-click="handleStopClick"
          @edit-stop="handleEditStop"
          @delete-stop="handleDeleteStop"
          @reorder-stops="handleReorderStops"
        />
      </ion-modal>

      <!-- Add/Edit Stop Modal -->
      <AddStopModal
        :is-open="showAddModal"
        :stop="editingStop"
        :default-lat="pendingLocation?.lat"
        :default-lng="pendingLocation?.lng"
        @close="closeAddModal"
        @save="handleSaveStop"
      />

      <!-- New Trip Modal -->
      <AppPanelModal :is-open="showNewTripModal" @did-dismiss="showNewTripModal = false">
        <ion-header>
          <ion-toolbar class="bg-gradient-to-r from-green-500 to-emerald-600">
            <ion-title class="text-white font-bold">
              <div class="flex items-center gap-2">
                <span class="text-2xl">🗺️</span>
                {{ t('newTrip') }}
              </div>
            </ion-title>
            <ion-buttons slot="end">
              <ion-button @click="showNewTripModal = false" class="text-white">
                ❌ {{ t('cancel') }}
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        <ion-content class="ion-padding bg-gradient-to-br from-green-50/30 to-emerald-50/30 dark:from-green-900/10 dark:to-emerald-900/10">
          <div class="max-w-md mx-auto mt-8">
            <div class="text-center mb-6">
              <div class="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                <span class="text-3xl">✨</span>
              </div>
              <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-2">Create New Trip</h2>
              <p class="text-sm text-gray-600 dark:text-gray-400">Plan your next adventure</p>
            </div>
            
            <div class="bg-white dark:bg-gray-800 rounded-lg p-4 border-2 border-green-200 dark:border-green-700 shadow-md">
              <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <span>🏷️</span> {{ t('tripName') }}
              </label>
              <ion-input
                v-model="newTripName"
                :placeholder="t('tripNamePlaceholder')"
                @keyup.enter="handleCreateNewTrip"
                class="custom-input"
              ></ion-input>
            </div>
            
            <ion-button 
              expand="block" 
              @click="handleCreateNewTrip"
              :disabled="!newTripName.trim()"
              class="mt-6 font-semibold shadow-lg"
              color="success"
            >
              <span class="text-lg">➕ {{ t('createTrip') }}</span>
            </ion-button>
          </div>
        </ion-content>
      </AppPanelModal>

      <!-- POI Search Modal -->
      <POISearchModal
        :is-open="showPOISearch"
        :current-location="mapCenter"
        :route-waypoints="routeWaypoints"
        @close="showPOISearch = false"
        @add-stop="handleAddPOIAsStop"
      />

      <!-- Vehicle Modal -->
      <VehicleModal
        :is-open="showVehicleModal"
        @close="showVehicleModal = false"
      />

      <!-- Trip Calculator (floating on mobile) -->
      <div v-if="displayStops.length > 1" class="lg:hidden fixed bottom-20 left-4 right-4 z-40">
        <TripCalculator
          :calculation="displayCalculation"
          :fuel-consumption="fuelConsumption"
          :fuel-price="fuelPrice"
          :stops="displayStops"
          :is-premium="authStore.isPremium"
          :vehicles="vehicleStore.vehicles"
          :selected-vehicle-id="currentTrip?.vehicleId ?? null"
          @update-fuel-settings="handleUpdateFuelSettings"
          @update:vehicle-id="handleVehicleChange"
          @add-vehicle="openVehicleModal"
        />
      </div>
    </ion-content>

    <!-- Loading -->
    <ion-loading
      :is-open="tripStore.isLoading"
      message="Loading..."
    />

    <!-- Add stop FAB button (only when authenticated) -->
    <ion-fab v-if="authStore.isAuthenticated" vertical="bottom" horizontal="end" slot="fixed" class="map-fab-lift lg:!right-[336px]" ref="fabRef">
      <ion-fab-button @click="handleFabClick" color="primary">
        <ion-icon :icon="addOutline"></ion-icon>
      </ion-fab-button>
      <ion-fab-list side="top" v-if="currentTrip">
        <ion-fab-button @click="openPOISearch" color="secondary" title="Search Places">
          <ion-icon :icon="searchOutline"></ion-icon>
        </ion-fab-button>
        <ion-fab-button @click="openAddStopModal" color="primary" title="Add Custom Stop">
          <ion-icon :icon="locationOutline"></ion-icon>
        </ion-fab-button>
      </ion-fab-list>
    </ion-fab>

    <!-- Mobile menu button -->
    <ion-fab vertical="bottom" horizontal="start" slot="fixed" class="lg:hidden">
      <ion-fab-button @click="showMobileSidebar = true">
        <ion-icon :icon="listOutline"></ion-icon>
      </ion-fab-button>
    </ion-fab>
  </ion-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
  IonModal,
  IonLoading,
  IonFab,
  IonFabButton,
  IonFabList,
  IonInput
} from '@ionic/vue'
import {
  addOutline,
  personOutline,
  listOutline,
  searchOutline,
  locationOutline,
  closeOutline,
  eyeOutline
} from 'ionicons/icons'
import { useAuthStore } from '@/stores/auth'
import { useTripStore } from '@/stores/trip'
import { useVehicleStore } from '@/stores/vehicle'
import MapView from '@/components/MapView.vue'
import StopsSidebar from '@/components/StopsSidebar.vue'
import StopsTimeline from '@/components/StopsTimeline.vue'
import TripSettings from '@/components/TripSettings.vue'
import AddStopModal from '@/components/AddStopModal.vue'
import POISearchModal from '@/components/POISearchModal.vue'
import TripCalculator from '@/components/TripCalculator.vue'
import TripMenu from '@/components/TripMenu.vue'
import VehicleModal from '@/components/VehicleModal.vue'
import type { Stop } from '@/types'
import { useI18n } from '@/i18n'
import { useSettingsStore } from '@/stores/settings'
import { storeToRefs } from 'pinia'
import { getDemoTrip, getDemoStops, getDemoCalculation } from '@/services/demo'
import AppPanelModal from '@/components/AppPanelModal.vue'

const router = useRouter()
const authStore = useAuthStore()
const tripStore = useTripStore()
const vehicleStore = useVehicleStore()
const { t } = useI18n()
const settingsStore = useSettingsStore()
const { language } = storeToRefs(settingsStore)

const mapRef = ref()
const fabRef = ref()
const showAddModal = ref(false)
const showMobileSidebar = ref(false)
const showPOISearch = ref(false)
const showNewTripModal = ref(false)
const showVehicleModal = ref(false)
const newTripName = ref('')
const editingStop = ref<Stop | null>(null)
const pendingLocation = ref<{ lat: number; lng: number } | null>(null)
const mapCenter = ref<{ lat: number; lng: number } | null>(null)

const currentTrip = computed(() => tripStore.currentTrip)

// Get the vehicle associated with the current trip, or default vehicle
const currentVehicle = computed(() => {
  if (!currentTrip.value) return null
  if (currentTrip.value.vehicleId) {
    return vehicleStore.vehicles.find(v => v.id === currentTrip.value!.vehicleId) || vehicleStore.defaultVehicle
  }
  return vehicleStore.defaultVehicle
})

// Get fuel consumption and price from current vehicle or use trip defaults
const fuelConsumption = computed(() => {
  return currentVehicle.value?.fuelConsumptionCombined || currentTrip.value?.fuelConsumption || 8.0
})

const fuelPrice = computed(() => {
  // Use trip's stored fuel price or default
  return currentTrip.value?.fuelPrice || 1.5
})

// Demo mode support
// Only show demo if NOT authenticated (regardless of trips)
// If authenticated, always show user's trips (even if empty)
const isInDemoMode = computed(() => !authStore.isAuthenticated)

const displayStops = computed(() => {
  if (isInDemoMode.value) {
    return getDemoStops()
  }
  return tripStore.sortedStops
})
const displayTrip = computed(() => {
  if (isInDemoMode.value) {
    return getDemoTrip()
  }
  return currentTrip.value
})
const displayCalculation = computed(() => {
  if (isInDemoMode.value) {
    return getDemoCalculation()
  }
  return tripStore.calculation
})

// Route waypoints for POI search along route
const routeWaypoints = computed(() => {
  return displayStops.value.map(stop => ({ lat: stop.lat, lng: stop.lng }))
})

// Reactive translations
const appName = computed(() => t('appName'))
const createFirstTripText = computed(() => t('createFirstTrip'))

onMounted(async () => {
  console.log('🏠 HomePage mounted')
  console.log('🏠 Auth status:', authStore.isAuthenticated)
  console.log('🏠 User:', authStore.user)
  console.log('🏠 Current trip:', tripStore.currentTrip)
  console.log('🏠 Demo mode:', isInDemoMode.value)
  
  // Load vehicles if user is authenticated
  if (authStore.user) {
    await vehicleStore.loadVehicles(authStore.user.id)
  }
  
  // Note: Trip store initialization is handled in main.ts
  // We only need to handle auth state changes here
})

// Watch for auth changes to reload trips or clear data
watch(() => authStore.user, async (newUser, oldUser) => {
  console.log('🔄 User changed:', newUser?.id, 'was:', oldUser?.id)
  
  if (newUser && !oldUser) {
    // User logged in - initialize trips and vehicles
    console.log('👤 User logged in, initializing for:', newUser.id)
    await Promise.all([
      tripStore.initialize(newUser.id),
      vehicleStore.loadVehicles(newUser.id)
    ])
  } else if (oldUser && !newUser) {
    // User logged out - clear trip data
    console.log('👋 User logged out, clearing trip data')
    tripStore.clearData()
  }
}, { immediate: false })

function handleMapClick(event: { lat: number; lng: number }) {
  // Don't allow adding stops if not authenticated
  if (!authStore.isAuthenticated) {
    return
  }
  
  pendingLocation.value = event
  showAddModal.value = true
}

function handleStopClick(stop: Stop) {
  // Pan map to stop location
  if (mapRef.value) {
    mapRef.value.panTo(stop.lat, stop.lng, 14)
  }
  
  // Close mobile sidebar
  showMobileSidebar.value = false
}

function resetRouteView() {
  if (mapRef.value?.fitToStops) {
    mapRef.value.fitToStops()
  }
}

function handleEditStop(stop: Stop) {
  // Prevent editing in demo mode
  if (isInDemoMode.value) {
    return
  }
  
  editingStop.value = stop
  pendingLocation.value = null
  showAddModal.value = true
}

async function handleDeleteStop(stopId: string) {
  // Prevent deleting in demo mode
  if (isInDemoMode.value) {
    return
  }
  
  if (confirm('Are you sure you want to delete this stop?')) {
    await tripStore.deleteStop(stopId)
  }
}

async function handleReorderStops(stopIds: string[]) {
  // Prevent reordering in demo mode
  if (isInDemoMode.value) {
    return
  }
  
  await tripStore.reorderStops(stopIds)
}

function closeAddModal() {
  showAddModal.value = false
  editingStop.value = null
  pendingLocation.value = null
}

async function handleSaveStop(stopData: Omit<Stop, 'id' | 'createdAt' | 'updatedAt'>) {
  console.log('🔹 handleSaveStop called with:', stopData)
  console.log('🔹 currentTrip:', currentTrip.value)
  console.log('🔹 authStore.user:', authStore.user)
  console.log('🔹 authStore.isAuthenticated:', authStore.isAuthenticated)
  
  try {
    // Ensure we have a current trip
    if (!currentTrip.value) {
      console.log('⚠️ No current trip, creating one...')
      
      // Check if user is authenticated
      if (!authStore.isAuthenticated || !authStore.user) {
        alert('Please sign in to save stops')
        router.push('/auth')
        return
      }
      
      // Create a default trip
      await tripStore.createTrip('My Trip', authStore.user.id)
      console.log('✅ Trip created:', tripStore.currentTrip)
    }
    
    if (editingStop.value) {
      // Update existing stop
      console.log('🔸 Updating stop:', editingStop.value.id)
      await tripStore.updateStop(editingStop.value.id, stopData)
    } else {
      // Add new stop
      const order = tripStore.sortedStops.length
      console.log('🔸 Adding new stop with order:', order)
      await tripStore.addStop({
        ...stopData,
        duration: stopData.duration ?? 60,
        order
      })
    }
    
    console.log('✅ Stop saved successfully')
    closeAddModal()
  } catch (error) {
    console.error('❌ Failed to save stop:', error)
    alert('Failed to save stop: ' + (error as Error).message)
  }
}

async function handleUpdateFuelSettings(settings: { consumption: number; price: number }) {
  if (currentTrip.value) {
    await tripStore.updateTrip({
      fuelConsumption: settings.consumption,
      fuelPrice: settings.price
    })
  }
}

async function handleVehicleChange(vehicleId: string | null) {
  if (!currentTrip.value || isInDemoMode.value) return
  
  // Get the selected vehicle
  const vehicle = vehicleId ? vehicleStore.vehicles.find(v => v.id === vehicleId) : null
  
  // Set default prices based on fuel type
  let defaultPrice = 1.5 // Default fuel price €/L
  if (vehicle?.fuelType === 'electric') {
    defaultPrice = 0.45 // Default electricity price €/kWh
  } else if (vehicle?.fuelType === 'diesel') {
    defaultPrice = 1.45 // Diesel is usually slightly cheaper
  } else if (vehicle?.fuelType === 'gasoline') {
    defaultPrice = 1.6 // Gasoline is usually slightly more expensive
  }
  
  // Update trip with new vehicle, its fuel consumption, and appropriate price
  await tripStore.updateTrip({
    vehicleId: vehicleId,
    fuelConsumption: vehicle?.fuelConsumptionCombined ?? 8.0,
    fuelPrice: defaultPrice
  })
  
  // Recalculate the trip with new fuel settings
  if (currentTrip.value.stops.length >= 2) {
    await tripStore.calculateTrip()
  }
}

function openVehicleModal() {
  showVehicleModal.value = true
}

async function closeFab() {
  // Close the FAB using Ionic's native API
  if (fabRef.value && fabRef.value.$el) {
    const fab = fabRef.value.$el
    if (fab.activated) {
      await fab.close()
    }
  }
}

function handleFabClick() {
  // If no current trip, open new trip modal
  if (!currentTrip.value) {
    showNewTripModal.value = true
    closeFab()
    return
  }
  
  // FAB handles its own toggle automatically
}

async function handleCreateNewTrip() {
  if (!newTripName.value.trim() || !authStore.user) return
  
  try {
    // Use default vehicle if available
    const defaultVehicleId = vehicleStore.defaultVehicle?.id
    const newTrip = await tripStore.createTrip(newTripName.value, authStore.user.id, defaultVehicleId)
    
    // If we have a default vehicle, update the trip with its fuel consumption
    if (vehicleStore.defaultVehicle) {
      await tripStore.updateTrip({
        fuelConsumption: vehicleStore.defaultVehicle.fuelConsumptionCombined,
        vehicleId: vehicleStore.defaultVehicle.id
      })
      await tripStore.calculateTrip()
    }
    
    // Close modal and reset
    showNewTripModal.value = false
    newTripName.value = ''
    
    // The new trip is automatically loaded by the store
    console.log('✅ New trip created and loaded:', tripStore.currentTrip?.name)
    console.log('✅ Using vehicle:', vehicleStore.defaultVehicle?.name)
  } catch (error) {
    console.error('❌ Failed to create trip:', error)
    alert('Failed to create trip: ' + (error as Error).message)
  }
}

// Watch for modal close to reset form
watch(showNewTripModal, (isOpen) => {
  if (!isOpen) {
    newTripName.value = ''
  }
})

function openPOISearch() {
  // Close FAB using native API
  closeFab()
  
  // Small delay to allow FAB animation to complete
  setTimeout(() => {
    // Get map center as current location
    if (mapRef.value && mapRef.value.getCenter) {
      mapCenter.value = mapRef.value.getCenter()
    }
    
    showPOISearch.value = true
  }, 100)
}

function openAddStopModal() {
  // Close FAB using native API
  closeFab()
  
  // Small delay to allow FAB animation to complete
  setTimeout(() => {
    showAddModal.value = true
  }, 100)
}

async function handleAddPOIAsStop(stopData: Partial<Stop>) {
  console.log('Adding POI as stop:', stopData)
  
  try {
    if (!currentTrip.value) {
      console.error('No current trip')
      return
    }
    
    const order = tripStore.sortedStops.length
    await tripStore.addStop({ 
      ...stopData,
      duration: stopData.duration ?? 60,
      tripId: currentTrip.value.id,
      userId: authStore.user!.id,
      order
    } as Omit<Stop, 'id' | 'createdAt' | 'updatedAt'>)
    
    console.log('✅ POI added as stop successfully')
  } catch (error) {
    console.error('❌ Failed to add POI as stop:', error)
    alert('Failed to add stop: ' + (error as Error).message)
  }
}
</script>

<style scoped>
/* HomePage specific styles */

/* Ensure ion-content takes full height */
ion-content {
  --background: transparent;
}

/* Main layout - horizontal flex on desktop */
.home-layout {
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  overflow: hidden;
}

/* Map section wrapper - contains map and timeline */
.map-section-wrapper {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* Map section takes remaining vertical space */
.map-section {
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  width: 100%;
}

/* Timeline section - horizontal stops bar at bottom */
.timeline-section {
  height: 140px;
  flex-shrink: 0;
  display: none; /* Hidden on mobile */
  overflow-x: auto;
  overflow-y: hidden;
}

/* Right sidebar - trip settings */
.settings-sidebar {
  width: 320px;
  max-width: 320px;
  height: 100%;
  flex-shrink: 0;
  display: none; /* Hidden on mobile */
  background: var(--ion-background-color, #fff);
  border-left: 1px solid var(--ion-border-color, #ddd);
  overflow-y: auto;
}

/* Show timeline and settings sidebar on large screens */
@media (min-width: 1024px) {
  .timeline-section {
    display: block;
  }
  
  .settings-sidebar {
    display: block;
  }
}

/* Mobile adjustments for FAB buttons */
@media (max-width: 1023px) {
  /* Adjust positioning if needed */
}

/* Utility classes */
.relative {
  position: relative;
}

.fixed {
  position: fixed;
}

.z-40 {
  z-index: 40;
}

.z-50 {
  z-index: 50;
}

.mt-4 {
  margin-top: 1rem;
}
</style>